import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { logUsage } from 'owa-analytics';
import { lightable } from 'owa-lightning-v2';
import { OutlookTeachingBubble } from 'owa-lightning-views';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import React from 'react';
import type { LightningProps } from 'owa-lightning-core-v2';
import type { Target } from '@fluentui/react-hooks';
import {
    priorityCalloutTitle,
    priorityCalloutDescription,
    priorityCalloutPrimaryButtonText,
    priorityCalloutSecondaryButtonText,
} from './PriorityInboxPivot.locstring.json';
import {
    getCopilotPrioritizeSettingsPath,
    type SettingsPath,
} from 'owa-options-copilot/lib/utils/getCopilotSettingsPath';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { lazyMountAndShowFullOptions } from 'owa-options-view';

export interface PriorityPivotTipProps extends LightningProps {
    id: 'PriorityPivot';
    target: Target;
}

const PriorityPivotTip = (props: PriorityPivotTipProps) => {
    const { target } = props;

    const settingsPath: SettingsPath = getCopilotPrioritizeSettingsPath();
    const mailboxInfo = getModuleContextMailboxInfo();

    React.useEffect(() => {
        logUsage('PriorityPivotTip_Shown', mailboxInfo);
    }, [mailboxInfo]);

    const onClickGotItButton = React.useCallback(() => {
        logUsage('PriorityPivotTip_GotIt', mailboxInfo);
    }, [mailboxInfo]);

    const onClickGoToSettingsButton = React.useCallback(() => {
        lazyMountAndShowFullOptions.importAndExecute(
            settingsPath.category,
            settingsPath.subcategory,
            undefined /* option */,
            undefined /* targetWindow */,
            mailboxInfo
        );

        logUsage('PriorityPivotTip_GoToSettings', mailboxInfo);
    }, [settingsPath, mailboxInfo]);

    const onDismissCallout = React.useCallback(() => {
        logUsage('PriorityPivotTip_Dismiss', mailboxInfo);
    }, [mailboxInfo]);

    const preventDismissOnEvent = React.useCallback(
        (ev: Event | React.FocusEvent | React.KeyboardEvent | React.MouseEvent): boolean => {
            if (ev.type === 'resize') {
                return false;
            }

            return true;
        },
        []
    );

    return (
        <OutlookTeachingBubble
            target={target}
            directionalHint={DirectionalHint.bottomLeftEdge}
            calloutTitle={loc(priorityCalloutTitle)}
            calloutDescription={loc(priorityCalloutDescription)}
            primaryButtonText={loc(priorityCalloutPrimaryButtonText)}
            secondaryButtonText={loc(priorityCalloutSecondaryButtonText)}
            lightningId="PriorityPivot"
            actionButtonOnClick={onClickGotItButton}
            notInterestedButtonOnClick={onClickGoToSettingsButton}
            onDismiss={onDismissCallout}
            setInitialFocus={true}
            preventDismissOnEvent={preventDismissOnEvent}
        ></OutlookTeachingBubble>
    );
};

const defaultExport: React.ComponentType<PriorityPivotTipProps & LightningProps> = lightable(
    observer(PriorityPivotTip, 'PriorityPivotTip')
);
export default defaultExport;
